import React, { useCallback, useEffect, useMemo, useState } from "react";
import { X, Loader2, Ticket, ShieldCheck, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../lib/events";
import { extractError } from "../lib/api";

const AU_MOBILE_RE = /^(?:\+?61|0)4\d{2}\s?\d{3}\s?\d{3}$/;

const ISSUE_TYPES = [
  { value: "complimentary", label: "Complimentary" },
  { value: "paid", label: "Manual payment received (paid)" },
  { value: "staff_sponsor", label: "Staff / sponsor allocation" },
  { value: "replacement", label: "Replacement" },
  { value: "other", label: "Other" },
];
const PAYMENT_METHODS = [
  { value: "cash", label: "Cash" },
  { value: "bank_transfer", label: "Bank transfer" },
  { value: "external_card", label: "External card" },
  { value: "eftpos", label: "EFTPOS" },
  { value: "other", label: "Other" },
];

const blankAttendee = () => ({ name: "", email: "", phone: "" });

export const ManualIssueDialog = ({ open, onClose, presetOrganiserId = "", presetEventId = "", presetOrganiserLabel = "", onIssued }) => {
  const [step, setStep] = useState("form"); // form | confirm
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loadError, setLoadError] = useState("");

  const [organisers, setOrganisers] = useState([]);
  const [events, setEvents] = useState([]);
  const [ticketTypes, setTicketTypes] = useState([]);

  const [organiserId, setOrganiserId] = useState(presetOrganiserId);
  const [eventId, setEventId] = useState(presetEventId);
  const [ticketTypeId, setTicketTypeId] = useState("");
  const [quantity, setQuantity] = useState(1);

  const [issueType, setIssueType] = useState("complimentary");
  const [reason, setReason] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [priceCents, setPriceCents] = useState("");
  const [discountCents, setDiscountCents] = useState("0");
  const [amountReceivedCents, setAmountReceivedCents] = useState("");
  const [paymentReference, setPaymentReference] = useState("");
  const [paymentDate, setPaymentDate] = useState("");
  const [adminNotes, setAdminNotes] = useState("");

  const [purchaser, setPurchaser] = useState({ first: "", last: "", email: "", phone: "" });
  const [attendees, setAttendees] = useState([blankAttendee()]);
  const [emailDelivery, setEmailDelivery] = useState("now");
  const [allowArchived, setAllowArchived] = useState(false);
  const idemKey = useMemo(() => `${Date.now()}-${Math.random().toString(36).slice(2)}`, [open]);

  const selectedTT = ticketTypes.find((t) => t.id === ticketTypeId);

  const loadOrganisers = useCallback(() => {
    setLoadError("");
    return adminApi.manualOrganisers("").then(setOrganisers)
      .catch((e) => setLoadError(extractError(e) || "Couldn't load organisers. Please retry."));
  }, []);
  const loadEvents = useCallback((orgId) => {
    if (!orgId) { setEvents([]); return Promise.resolve(); }
    setLoadError("");
    return adminApi.manualEvents(orgId).then(setEvents)
      .catch((e) => { setEvents([]); setLoadError(extractError(e) || "Couldn't load events. Please retry."); });
  }, []);
  const loadTicketTypes = useCallback((evId) => {
    if (!evId) { setTicketTypes([]); return Promise.resolve(); }
    setLoadError("");
    return adminApi.manualTicketTypes(evId).then((d) => setTicketTypes(d.ticket_types || []))
      .catch((e) => { setTicketTypes([]); setLoadError(extractError(e) || "Couldn't load ticket types. Please retry."); });
  }, []);
  const retryLoads = () => {
    loadOrganisers();
    if (organiserId) loadEvents(organiserId);
    if (eventId) loadTicketTypes(eventId);
  };
  useEffect(() => { if (open) loadOrganisers(); }, [open, loadOrganisers]);
  useEffect(() => {
    // Reset transient form state each time the dialog opens (avoid stale data).
    if (!open) return;
    setStep("form"); setPreview(null); setBusy(false);
    setOrganiserId(presetOrganiserId); setEventId(presetEventId); setTicketTypeId("");
    setQuantity(1); setIssueType("complimentary"); setReason("");
    setPaymentMethod("cash"); setPriceCents(""); setDiscountCents("0"); setAmountReceivedCents("");
    setPaymentReference(""); setPaymentDate(""); setAdminNotes("");
    setPurchaser({ first: "", last: "", email: "", phone: "" });
    setAttendees([blankAttendee()]); setEmailDelivery("now"); setAllowArchived(false);
  }, [open, presetOrganiserId, presetEventId]);
  useEffect(() => { loadEvents(organiserId); }, [organiserId, loadEvents]);
  useEffect(() => { loadTicketTypes(eventId); }, [eventId, loadTicketTypes]);
  useEffect(() => {
    const q = Math.max(1, Math.min(100, parseInt(quantity, 10) || 1));
    setAttendees((prev) => {
      const next = prev.slice(0, q);
      while (next.length < q) next.push(blankAttendee());
      return next;
    });
  }, [quantity]);

  if (!open) return null;

  const isPaid = issueType === "paid";
  const needReason = issueType === "other" || paymentMethod === "other";

  const setAttendee = (i, k, v) => setAttendees((p) => p.map((a, idx) => (idx === i ? { ...a, [k]: v } : a)));
  const copyPurchaserToAttendee = (i) =>
    setAttendees((p) => p.map((a, idx) => (idx === i
      ? { ...a, name: `${purchaser.first} ${purchaser.last}`.trim(), email: purchaser.email, phone: purchaser.phone }
      : a)));
  const copyPurchaserToAll = () =>
    setAttendees((p) => p.map((a) => ({ ...a, name: `${purchaser.first} ${purchaser.last}`.trim(), email: purchaser.email, phone: purchaser.phone })));

  const buildPayload = () => ({
    idempotency_key: idemKey,
    organiser_id: organiserId,
    event_id: eventId,
    ticket_type_id: ticketTypeId,
    quantity: parseInt(quantity, 10) || 1,
    purchaser_first_name: purchaser.first,
    purchaser_last_name: purchaser.last,
    purchaser_email: purchaser.email,
    purchaser_phone: purchaser.phone,
    attendees: attendees.map((a) => ({ name: a.name, email: a.email || null, phone: a.phone || null })),
    manual_issue_type: issueType,
    manual_issue_reason: reason || null,
    manual_payment_method: isPaid ? paymentMethod : null,
    manual_price_cents: isPaid ? Math.round(parseFloat(priceCents || "0") * 100) : null,
    manual_discount_cents: isPaid ? Math.round(parseFloat(discountCents || "0") * 100) : null,
    manual_amount_received_cents: isPaid ? Math.round(parseFloat(amountReceivedCents || "0") * 100) : null,
    manual_payment_reference: isPaid ? paymentReference || null : null,
    manual_payment_date: isPaid ? paymentDate || null : null,
    admin_notes: adminNotes || null,
    email_delivery: emailDelivery,
    allow_archived_override: allowArchived,
  });

  const validateForm = () => {
    if (!organiserId || !eventId || !ticketTypeId) return "Select organiser, event and ticket type.";
    if (!purchaser.first || !purchaser.last || !purchaser.email || !purchaser.phone) return "Complete all purchaser details.";
    if (!AU_MOBILE_RE.test((purchaser.phone || "").trim())) return "Enter a valid Australian mobile (e.g. 04xx xxx xxx).";
    if (attendees.some((a) => !a.name.trim())) return "Every attendee needs a name.";
    if (needReason && !reason.trim()) return "A reason is required for 'Other'.";
    if (isPaid && (!priceCents || amountReceivedCents === "")) return "Enter ticket price and amount received.";
    return null;
  };

  const goConfirm = async () => {
    const err = validateForm();
    if (err) { toast.error(err); return; }
    setBusy(true);
    try {
      const p = await adminApi.manualPreview(buildPayload());
      setPreview(p);
      setStep("confirm");
    } catch (e) {
      toast.error(extractError(e) || "Could not validate. Check the details.");
    } finally { setBusy(false); }
  };

  const doIssue = async () => {
    setBusy(true);
    try {
      const res = await adminApi.manualIssue(buildPayload());
      toast.success(`Issued ${res.ticket_count} ticket(s) · ${res.status}${res.email_status === "sent" ? " · emailed" : ""}`);
      onIssued && onIssued(res);
      onClose();
    } catch (e) {
      toast.error(extractError(e) || "Failed to issue tickets.");
    } finally { setBusy(false); }
  };

  const money = (c) => `A$${((c || 0) / 100).toFixed(2)}`;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4" data-testid="manual-issue-dialog">
      <div className="my-8 w-full max-w-2xl bg-white border border-[var(--gt-line)] shadow-xl">
        <div className="flex items-center justify-between border-b border-[var(--gt-line)] px-6 py-4">
          <div className="flex items-center gap-2">
            <Ticket size={18} className="text-[var(--gt-accent)]" />
            <h2 className="font-heading text-lg font-black tracking-tight">Add Attendee / Issue Ticket</h2>
          </div>
          <button onClick={onClose} data-testid="manual-issue-close" className="text-[var(--gt-ink-3)] hover:text-[var(--gt-ink)]"><X size={20} /></button>
        </div>

        {step === "form" && (
          <div className="max-h-[70vh] overflow-y-auto px-6 py-5 space-y-5 text-sm">
            {loadError && (
              <div role="alert" data-testid="manual-load-error" className="flex items-center justify-between gap-3 border border-[var(--gt-accent)] bg-red-50 text-[var(--gt-accent)] text-sm px-3 py-2">
                <span>{loadError}</span>
                <button type="button" onClick={retryLoads} data-testid="manual-load-retry" className="underline font-semibold shrink-0">Retry</button>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Field label="Organiser">
                <select data-testid="manual-organiser-select" className="gt-input" value={organiserId}
                  onChange={(e) => { setOrganiserId(e.target.value); setEventId(""); setTicketTypeId(""); }}>
                  <option value="">Select…</option>
                  {organiserId && !organisers.some((o) => o.id === organiserId) && (
                    <option value={organiserId}>{presetOrganiserLabel || organiserId}</option>
                  )}
                  {organisers.map((o) => <option key={o.id} value={o.id}>{o.name || o.email}</option>)}
                </select>
              </Field>
              <Field label="Event">
                <select data-testid="manual-event-select" className="gt-input" value={eventId}
                  onChange={(e) => { setEventId(e.target.value); setTicketTypeId(""); }} disabled={!organiserId}>
                  <option value="">Select…</option>
                  {events.map((ev) => <option key={ev.id} value={ev.id}>{ev.title} ({ev.status})</option>)}
                </select>
              </Field>
              <Field label="Ticket type">
                <select data-testid="manual-tickettype-select" className="gt-input" value={ticketTypeId}
                  onChange={(e) => setTicketTypeId(e.target.value)} disabled={!eventId}>
                  <option value="">Select…</option>
                  {ticketTypes.map((t) => <option key={t.id} value={t.id}>{t.name} · {t.available} left{t.archived ? " · archived" : ""}</option>)}
                </select>
              </Field>
            </div>
            {selectedTT?.archived && (
              <label className="flex items-center gap-2 text-xs text-[var(--gt-accent)]">
                <input type="checkbox" data-testid="manual-archived-override" checked={allowArchived} onChange={(e) => setAllowArchived(e.target.checked)} />
                Override: issue against this archived ticket type
              </label>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Field label="Quantity">
                <input data-testid="manual-quantity" type="number" min={1} max={100} className="gt-input" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
              </Field>
              <Field label="Issue type">
                <select data-testid="manual-issuetype-select" className="gt-input" value={issueType} onChange={(e) => setIssueType(e.target.value)}>
                  {ISSUE_TYPES.filter((t) => t.value !== "replacement").map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </Field>
              <Field label="Email delivery">
                <select data-testid="manual-email-delivery" className="gt-input" value={emailDelivery} onChange={(e) => setEmailDelivery(e.target.value)}>
                  <option value="now">Send immediately</option>
                  <option value="none">Create without sending</option>
                </select>
              </Field>
            </div>

            {needReason && (
              <Field label="Reason (required)">
                <input data-testid="manual-reason" className="gt-input" value={reason} onChange={(e) => setReason(e.target.value)} />
              </Field>
            )}

            {isPaid && (
              <div className="border border-[var(--gt-line)] p-4 space-y-3">
                <div className="gt-label">Payment recorded (off-platform)</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <Field label="Method">
                    <select data-testid="manual-payment-method" className="gt-input" value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
                      {PAYMENT_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </select>
                  </Field>
                  <Field label="Ticket price (A$)"><input data-testid="manual-price" className="gt-input" value={priceCents} onChange={(e) => setPriceCents(e.target.value)} /></Field>
                  <Field label="Discount (A$)"><input className="gt-input" value={discountCents} onChange={(e) => setDiscountCents(e.target.value)} /></Field>
                  <Field label="Amount received (A$)"><input data-testid="manual-amount-received" className="gt-input" value={amountReceivedCents} onChange={(e) => setAmountReceivedCents(e.target.value)} /></Field>
                  <Field label="Reference"><input className="gt-input" value={paymentReference} onChange={(e) => setPaymentReference(e.target.value)} /></Field>
                  <Field label="Payment date"><input type="date" className="gt-input" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} /></Field>
                </div>
              </div>
            )}

            <div className="border border-[var(--gt-line)] p-4 space-y-3">
              <div className="gt-label">Purchaser details</div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="First name"><input data-testid="manual-purchaser-first" className="gt-input" value={purchaser.first} onChange={(e) => setPurchaser({ ...purchaser, first: e.target.value })} /></Field>
                <Field label="Last name"><input data-testid="manual-purchaser-last" className="gt-input" value={purchaser.last} onChange={(e) => setPurchaser({ ...purchaser, last: e.target.value })} /></Field>
                <Field label="Email"><input data-testid="manual-purchaser-email" className="gt-input" value={purchaser.email} onChange={(e) => setPurchaser({ ...purchaser, email: e.target.value })} /></Field>
                <Field label="Australian mobile"><input data-testid="manual-purchaser-phone" className="gt-input" value={purchaser.phone} onChange={(e) => setPurchaser({ ...purchaser, phone: e.target.value })} placeholder="04xx xxx xxx" /></Field>
              </div>
            </div>

            <div className="border border-[var(--gt-line)] p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="gt-label">Attendees ({attendees.length})</div>
                <button type="button" onClick={copyPurchaserToAll} className="text-xs underline" data-testid="manual-copy-all">Copy purchaser to all</button>
              </div>
              {attendees.map((a, i) => (
                <div key={i} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_1fr_auto] gap-2 items-end">
                  <Field label={`Attendee ${i + 1} name`}><input data-testid={`manual-attendee-name-${i}`} className="gt-input" maxLength={120} value={a.name} onChange={(e) => setAttendee(i, "name", e.target.value)} /></Field>
                  <Field label="Email (optional)"><input data-testid={`manual-attendee-email-${i}`} className="gt-input" maxLength={254} value={a.email} onChange={(e) => setAttendee(i, "email", e.target.value)} /></Field>
                  <Field label="Phone (optional)"><input data-testid={`manual-attendee-phone-${i}`} className="gt-input" maxLength={32} value={a.phone} onChange={(e) => setAttendee(i, "phone", e.target.value)} /></Field>
                  <button type="button" onClick={() => copyPurchaserToAttendee(i)} className="gt-btn-ghost text-xs mb-1" data-testid={`manual-copy-${i}`}>Copy purchaser</button>
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button onClick={onClose} className="gt-btn-ghost">Cancel</button>
              <button onClick={goConfirm} disabled={busy || !organiserId || !eventId || !ticketTypeId} className="gt-btn-primary" data-testid="manual-review-btn">
                {busy && <Loader2 size={14} className="animate-spin" />} Review
              </button>
            </div>
          </div>
        )}

        {step === "confirm" && preview && (
          <div className="px-6 py-5 space-y-4 text-sm" data-testid="manual-confirm-panel">
            <div className="gt-label">Confirm and issue</div>
            <div className="border border-[var(--gt-line)] divide-y divide-[var(--gt-line)]">
              <Row k="Organiser" v={preview.organiser?.name || preview.organiser?.email} />
              <Row k="Event" v={preview.event?.title} />
              <Row k="Ticket type" v={preview.ticket_type?.name} />
              <Row k="Quantity" v={preview.quantity} />
              <Row k="Attendees" v={(preview.attendee_names || []).join(", ")} />
              <Row k="Issue type" v={preview.manual_issue_type} />
              {preview.manual_payment_method && <Row k="Payment method" v={preview.manual_payment_method} />}
              <Row k="Recorded amount" v={money(preview.financials?.amount_total_cents)} />
              <Row k="Email" v={preview.email_delivery === "now" ? "Send immediately" : "Create without sending"} />
              <Row k="Capacity remaining after" v={preview.available_after} highlight={!preview.capacity_ok} />
            </div>
            {!preview.capacity_ok && <div className="text-[var(--gt-accent)] text-xs" role="alert" data-testid="manual-capacity-warning">Not enough capacity for this quantity.</div>}
            <div className="flex justify-between gap-2 pt-2">
              <button onClick={() => setStep("form")} className="gt-btn-ghost"><ArrowLeft size={14} /> Back</button>
              <button onClick={doIssue} disabled={busy || !preview.capacity_ok} className="gt-btn-primary" data-testid="manual-confirm-issue-btn">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />} Confirm and issue tickets
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-[11px] uppercase tracking-wider text-[var(--gt-ink-3)] mb-1">{label}</span>
    {children}
  </label>
);

const Row = ({ k, v, highlight }) => (
  <div className="flex justify-between px-3 py-2">
    <span className="text-[var(--gt-ink-3)]">{k}</span>
    <span className={highlight ? "text-[var(--gt-accent)] font-semibold" : "font-medium"}>{String(v)}</span>
  </div>
);

export default ManualIssueDialog;
